import { _GettersTree } from 'pinia';
import { AnalyticsEventTypes } from '../../enums/AnalyticsEvents';
import { UrlParameters } from '../../enums/UrlParameters';
import { ICarSelectStoreState } from '../../interfaces/ICarSelectStoreState';
import { YEAR_FILTER } from '../../appConstants';
import { IMakeModelData } from '../../interfaces/IMakeModelData';
import { getMakeModelDataFilter } from '../../helpers';

type SingleMakeFilterType = {
  op: string;
  children: {
    field: string;
    eq: string[] | number[];
  }[];
};

/**
 *
 * @param fieldName
 * @param selectedList
 * @param currentSearchCriteria
 * @param spreadValue
 * @param onlyMinMax
 * @param orderAscending If true, then the values added to the search criteria will be in ascending order (smallest -> largest)
 * @param applyRangeSearchFix If true and only two values are passed the first value is duplicated in order to have the AMS range search behave as an exact match search
 */
function appendToSharedCriteria(
  fieldName: string,
  selectedList: any,
  currentSearchCriteria: any,
  spreadValue = false,
  onlyMinMax = false,
  orderAscending = false,
  applyRangeSearchFix = false
) {
  let listOfValues: any[] = selectedList.reduce((accumulatedListOfValues: any, currentSelectedItem: any) => {
    if (spreadValue) {
      return [...accumulatedListOfValues, ...currentSelectedItem.value];
    } else {
      return [...accumulatedListOfValues, currentSelectedItem.value];
    }
  }, []);

  if (listOfValues.length > 0) {
    if (onlyMinMax) {
      listOfValues = [Math.min(...listOfValues), Math.max(...listOfValues)];
    }

    if (typeof listOfValues[0] === 'number') {
      if (orderAscending) {
        // Sort first, so we have the correct order if also applyRangeSearchFix
        listOfValues.sort((a: number, b: number) => a - b);
      }

      if (applyRangeSearchFix && listOfValues.length === 2) {
        // Duplicate the first element of the array ([2,7] -> [2,2,7])
        listOfValues.splice(1, 0, listOfValues[0]);
      }
    }

    currentSearchCriteria.push({ field: fieldName, eq: listOfValues });
  }
}

function appendFeatureToSharedCriteria(selectedList: any, currentSearchCriteria: any) {
  selectedList.forEach((element: any) => {
    if (element.field && element.field.includes('|')) {
      //if there are multiple filters - belonging to the same field.
      const fields = element.field.split('|');
      let children: any[] = [];
      fields.forEach((field: any) => {
        children.push({ field: field, eq: element.value.includes('|') ? element.value.split('|') : [element.value] });
      });
      currentSearchCriteria.push({ op: 'or', children: children });
    } else {
      currentSearchCriteria.push({ field: element.field, eq: element.value.includes('|') ? element.value.split('|') : [element.value] });
    }
  });
}

/**
 *
 * @param rangeValues
 * @returns
 */
function constructRangeSharedCriteria(rangeValues: any) {
  const constructedRangeObject = [];

  if (rangeValues.min || rangeValues.max) {
    let rangeValueParsed: number | null = parseInt(rangeValues.min);

    if (isNaN(rangeValueParsed)) {
      rangeValueParsed = null;
    }

    constructedRangeObject.push(rangeValueParsed);

    rangeValueParsed = parseInt(rangeValues.max);

    if (isNaN(rangeValueParsed)) {
      rangeValueParsed = null;
    }

    constructedRangeObject.push(rangeValueParsed);
  }

  return constructedRangeObject;
}

/**
 *
 * @param fieldName
 * @param selectedList
 * @param currentSearchCriteria
 */
function appendRangeToSharedCriteria(fieldName: string, selectedList: any, currentSearchCriteria: any) {
  const rangeValues = constructRangeSharedCriteria(selectedList);
  if (rangeValues.length > 0) {
    currentSearchCriteria.push({ field: fieldName, eq: rangeValues });
  }
}
/**
 *
 * @param state
 * @returns
 */
function getSharedSearchCriteria(state: ICarSelectStoreState) {
  // Create the shared criteria
  const sharedCriteria = [];

  // year we have to query two fields and use an OR operator
  const yearChildren = [];
  const yearRange = constructRangeSharedCriteria(state.selectedYear);
  if (yearRange.length > 0) {
    yearChildren.push({ field: 'year_low', eq: yearRange });
    yearChildren.push({ field: 'year_high', eq: yearRange });
    sharedCriteria.push({ op: 'or', children: yearChildren });
  }

  // Additional price range handling to cater for different data points for current vs discontinued models
  if (state.selectedPrice?.min || state.selectedPrice?.max) {
    const priceRangeFilterCurrent = {
      op: 'and',
      children: [
        { field: 'qld_driveawayprice', eq: [state.selectedPrice?.min || 0, state.selectedPrice?.max || 9999999] },
        { field: 'vehicle_status', eq: ['current'] },
      ],
    };
    const priceRangeFilterDiscontinued = {
      op: 'and',
      children: [
        { field: 'price_current_retail_value_average', eq: [state.selectedPrice?.min || 0, state.selectedPrice?.max || 9999999] },
        { field: 'vehicle_status', eq: ['discontinued'] },
      ],
    };
    const priceFilter = {
      op: 'or',
      children: [priceRangeFilterCurrent, priceRangeFilterDiscontinued],
    };
    sharedCriteria.push(priceFilter);
  }

  const seatsTemp = Array.from(state.selectedSeats);
  const doorsTemp = Array.from(state.selectedDoors);

  // 1. Append values to Seats search criteria for 8 or more (doors don't matter). Could be refactored - see JIRA 697 above.
  if (seatsTemp.findIndex((seat) => seat.value === 8) > -1) {
    for (let i = 9; i < 21; i++) {
      seatsTemp.push({ name: `temp${i}`, value: i });
    }
  }
  // #######################################################################

  appendToSharedCriteria('vehicle_seats', seatsTemp, sharedCriteria, false, false, true, true); // RangeSearchFix
  appendToSharedCriteria('vehicle_doors', doorsTemp, sharedCriteria, false, false, true, true); // RangeSearchFix
  appendToSharedCriteria('environment_score', state.selectedEmissionsRatings, sharedCriteria, true, false);
  appendToSharedCriteria('ancap_overall_score', state.selectedSafetyRatings, sharedCriteria, false, false, true, true); // RangeSearchFix

  //appendRangeToSharedCriteria('retail_price', state.selectedPrice, sharedCriteria);
  appendToSharedCriteria('towing_capacity', state.selectedTowingCapacities, sharedCriteria, true, true);
  appendToSharedCriteria('engine_combustion_capacity_lt', state.selectedEngineCapacities, sharedCriteria, true, true);
  appendToSharedCriteria('body_type', state.selectedBodyTypes, sharedCriteria, true);
  appendToSharedCriteria('fuel_type', state.selectedFuelTypes, sharedCriteria);
  appendToSharedCriteria('driving_range_electric_motor_only_km', state.selectedDrivingRanges, sharedCriteria, true, true);
  appendToSharedCriteria('vehicle_transmission_type', state.selectedTransmissionTypes, sharedCriteria);
  appendToSharedCriteria('driven_wheels', state.selectedDriveTypes, sharedCriteria);
  appendToSharedCriteria('engine_type', state.selectedEngineTypes, sharedCriteria, true, false); // TODO column name to be confirmed
  appendToSharedCriteria('engine_combustion_cylinders', state.selectedCylinders, sharedCriteria, false, false, true, true); // RangeSearchFix
  appendToSharedCriteria('engine_fuel_consumption_combined_adr8102_lt_100km', state.selectedFuelScores, sharedCriteria, true, true);

  appendFeatureToSharedCriteria(state.selectedFeatures, sharedCriteria);

  // grab the lifestyles if any (lifestyles use a different structure)
  state.selectedLifestyles.forEach((currentLifestyle) => {
    sharedCriteria.push({ tag: currentLifestyle.value });
  });

  return sharedCriteria;
}

/**
 *
 * @param listToSearch
 * @param fieldName
 * @param makeName
 * @returns
 */
function getModelOrBadgeForMake(listToSearch: any, fieldName: string, makeName: string): any[] {
  const itemsForMake = listToSearch.reduce((accumulatedItems: any, currentItem: any) => {
    if (currentItem.makeName === makeName) {
      return [...accumulatedItems, currentItem[fieldName]];
    }
    return accumulatedItems;
  }, []);
  return itemsForMake;
}

/**
 *
 * @param parameterName
 * @param selectedList
 * @param isRange
 * @returns
 */
function constructUrlQueryParameter(parameterName: string, selectedList: any, isRange = false) {
  let urlToReturn = '';
  if (isRange) {
    if (selectedList.min || selectedList.max) urlToReturn = `&${parameterName}=${selectedList.min ?? ''}_${selectedList.max ?? ''}`;
  } else {
    if (selectedList.length > 0) urlToReturn = `&${parameterName}=${encodeURIComponent(selectedList.map((item: any) => item.name).join('_'))}`;
  }

  return urlToReturn;
}
/**
 * Get filters for make and model
 * @param makeName current make
 * @param selectedModels already selected model in store
 * @param make
 * @returns
 */
function getFiltersForMakeModel(makeName: string, selectedModels?: any, make?: IMakeModelData): any {
  const singleMakeFilter: SingleMakeFilterType = { op: 'and', children: [{ field: 'vehicle_make', eq: [makeName.toLowerCase()] }] };

  // grab the models for the make if any
  const modelsForMake = make
    ? make.model.map((element) => {
        return element.toLowerCase();
      })
    : getModelOrBadgeForMake(selectedModels, 'modelName', makeName).map((element) => {
        return element.toLowerCase();
      });

  if (modelsForMake.length > 0) {
    singleMakeFilter.children.push({ field: 'vehicle_model', eq: modelsForMake });
  }
  return singleMakeFilter;
}

/**
 *
 */
export const carAdvisoryGetters: Record<string, any> = {
  hasSearchCriteria(state: ICarSelectStoreState) {
    // TODO: this doesn't cater for filters other than make/model/badge
    return state.selectedMakes.length > 0 || state.selectedModels.length > 0 || state.selectedBadges.length > 0;
  },
  /**
   *
   * @param state
   * @returns
   */
  getFiltersForAnalytics(state: ICarSelectStoreState) {
    // Create the shared criteria
    var filters = {
      onsiteSearchType: {},
      onsiteSearchResults: {},
      searchInfo: {
        selectedMakes: {},
        selectedModels: {},
        selectedBadges: {},
        selectedYear: {},
        selectedPrice: {},
        selectedTowingCapacities: {},
        selectedBodyTypes: {},
        selectedFuelTypes: {},
      },
    };
    filters.searchInfo.selectedMakes = state.selectedMakes;
    filters.searchInfo.selectedModels = state.selectedModels;
    filters.searchInfo.selectedBadges = state.selectedBadges;
    filters.searchInfo.selectedYear = state.selectedYear;
    filters.searchInfo.selectedPrice = state.selectedPrice;
    filters.searchInfo.selectedTowingCapacities = state.selectedTowingCapacities;
    filters.searchInfo.selectedBodyTypes = state.selectedBodyTypes;
    filters.searchInfo.selectedFuelTypes = state.selectedFuelTypes;
    filters.onsiteSearchType = AnalyticsEventTypes.CarSearch;
    filters.onsiteSearchResults = 0; //get set after results are fetched
    return filters;
  },
  getUrlSearchCriteria: (state: ICarSelectStoreState) => {
    let queryString = '';

    // the url will follow this specific order

    // construct the year
    queryString += `?${UrlParameters.Year}=${state.selectedYear.min ?? ''}_${state.selectedYear.max ?? ''}`;

    // add the make, model and badges
    if (state.selectedMakes.length > 0) {
      queryString += `&${UrlParameters.Make}=${encodeURIComponent(state.selectedMakes.join('_'))}`;
    }

    const modelCriteria: any[] = [];
    const badgeCriteria: any[] = [];

    state.selectedMakes.forEach((makeName) => {
      // grab the models for the make if any
      const badgeModelCriteria: any[] = [];
      const modelsForMake = getModelOrBadgeForMake(state.selectedModels, 'modelName', makeName).map((modelName) => {
        // calculate the badges as well so we don't have to loop again
        const badgesForMakeModel = state.selectedBadges.filter((badgeItem) => badgeItem.makeName === makeName && badgeItem.modelName === modelName);
        badgeModelCriteria.push(badgesForMakeModel.map((badgeItem) => badgeItem.badgeName).join(','));
        return modelName;
      });
      modelCriteria.push(modelsForMake.join(';'));
      badgeCriteria.push(badgeModelCriteria.join(';'));
    });

    if (state.selectedModels.length > 0) queryString += `&${UrlParameters.Model}=${encodeURIComponent(modelCriteria.join('_'))}`;
    if (state.selectedBadges.length > 0) queryString += `&${UrlParameters.Badge}=${encodeURIComponent(badgeCriteria.join('_'))}`;

    queryString += constructUrlQueryParameter(UrlParameters.Price, state.selectedPrice, true);
    queryString += constructUrlQueryParameter(UrlParameters.Seat, state.selectedSeats, false);
    queryString += constructUrlQueryParameter(UrlParameters.Door, state.selectedDoors, false);
    queryString += constructUrlQueryParameter(UrlParameters.BodyType, state.selectedBodyTypes);
    queryString += constructUrlQueryParameter(UrlParameters.Lifestyle, state.selectedLifestyles);
    queryString += constructUrlQueryParameter(UrlParameters.FuelType, state.selectedFuelTypes);
    queryString += constructUrlQueryParameter(UrlParameters.TowingCapacity, state.selectedTowingCapacities);
    queryString += constructUrlQueryParameter(UrlParameters.EmissionsRating, state.selectedEmissionsRatings);
    queryString += constructUrlQueryParameter(UrlParameters.DrivingRange, state.selectedDrivingRanges);
    queryString += constructUrlQueryParameter(UrlParameters.TransmissionType, state.selectedTransmissionTypes);
    queryString += constructUrlQueryParameter(UrlParameters.DriveType, state.selectedDriveTypes);
    queryString += constructUrlQueryParameter(UrlParameters.EngineType, state.selectedEngineTypes);
    queryString += constructUrlQueryParameter(UrlParameters.Cylinder, state.selectedCylinders);
    queryString += constructUrlQueryParameter(UrlParameters.EngineCapacity, state.selectedEngineCapacities);
    queryString += constructUrlQueryParameter(UrlParameters.FuelScore, state.selectedFuelScores);
    queryString += constructUrlQueryParameter(UrlParameters.SafetyRating, state.selectedSafetyRatings);
    queryString += constructUrlQueryParameter(UrlParameters.Feature, state.selectedFeatures);

    return queryString;
  },
  getSortOrder: (state: ICarSelectStoreState) => {
    return () => (state.selectedSortOrder.length > 0 ? state.selectedSortOrder : []);
  },
  getSearchCriteria: (state: ICarSelectStoreState) => {
    const yearLowFilter = { field: 'year_low', eq: [YEAR_FILTER.minYearAllowed, 2100] };
    const yearHighFilter = { field: 'year_high', eq: [YEAR_FILTER.minYearAllowed, 2100] };
    const yearFilter = {
      op: 'or',
      children: [yearLowFilter, yearHighFilter],
    };

    const mainFilter = {
      op: 'and',
      children: getSharedSearchCriteria(state) as any[],
    };
    mainFilter.children.push(yearFilter);

    const makesFilter = { op: 'or', children: [] as any[] };
    state.selectedMakes.forEach((makeName: string) => {
      const singleMakeFilter: SingleMakeFilterType = { op: 'and', children: [{ field: 'vehicle_make', eq: [makeName.toLowerCase()] }] };

      // grab the models for the make if any
      const modelsForMake = getModelOrBadgeForMake(state.selectedModels, 'modelName', makeName).map((element) => {
        return element.toLowerCase();
      });

      if (modelsForMake.length > 0) {
        singleMakeFilter.children.push({ field: 'vehicle_model', eq: modelsForMake });
      }

      // grab the badges for the make if any
      const badgesForMake = getModelOrBadgeForMake(state.selectedBadges, 'badgeName', makeName).map((element) => {
        const badgeName = element.toLowerCase();
        if (badgeName === 'other variants') {
          return '-';
        }
        return badgeName;
      });

      if (badgesForMake.length > 0) {
        singleMakeFilter.children.push({ field: 'vehicle_trim', eq: badgesForMake });
      }
      makesFilter.children.push(singleMakeFilter);
    });
    if (makesFilter.children?.length > 0) {
      mainFilter.children.push(makesFilter);
    }
    return () => mainFilter;
  },
  /**
   * This function is used to search based on make and model in the search filter
   * or make and models displayed on the listing
   * @param state State of the store
   * @returns filters for API
   */
  getSearchCriteriaForMakeAndModel: (state: ICarSelectStoreState) => {
    return (cars: any[]) => {
      const mainFilter = { op: 'and', children: [] as any[] };
      const allMakesFilter = { op: 'or', children: [] as any[] };

      state.selectedMakes.forEach((makeName: string) => {
        const singleMakeFilter = getFiltersForMakeModel(makeName, state.selectedModels);
        allMakesFilter.children.push(singleMakeFilter);
      });

      if (allMakesFilter.children.length > 0) {
        mainFilter.children.push(allMakesFilter);
      } else {
        const makeModelData = getMakeModelDataFilter(cars);
        makeModelData.forEach((make: IMakeModelData) => {
          const makeName = make.make;
          const singleMakeFilter = getFiltersForMakeModel(makeName, null, make);
          allMakesFilter.children.push(singleMakeFilter);
        });
        if (allMakesFilter.children.length > 0) {
          mainFilter.children.push(allMakesFilter);
        }
      }
      return () => mainFilter;
    };
  },
  valueExistsInState(state: ICarSelectStoreState) {
    return (stateKey = '') => {
      if (!stateKey) return false;

      return Object.values(this[stateKey]).some((val) => Boolean(val));
    };
  },
};
